import WarningAmberRoundedIcon from "@mui/icons-material/WarningAmberRounded";
import type { Theme } from "@mui/material";
import { Chip, Collapse, Link, Stack, TableBody, TableCell, TableHead, TableRow, Typography } from "@mui/material";
import { Link as RouterLink } from "react-router";
import ExpandCollapseButton from "@entrilia/shared/components/ExpandCollapseButton";
import RoundedTable from "@entrilia/shared/components/RoundedTable";
import StatusChip from "@entrilia/shared/components/StatusChip";
import UserAvatar from "@entrilia/shared/components/UserAvatar";
import CollapseIcon from "@entrilia/shared/icons/CollapseIcon";
import ExpandIcon from "@entrilia/shared/icons/ExpandIcon";
import { makeTintedBackgroundFromColor } from "@entrilia/shared/utilities/colorHelper";
import { pluralize } from "@entrilia/shared/utilities/stringHelper";
import type { MemberChangeAction } from "../../../../../api/types/workflowTypes";
import ExistingContactWidget from "./ExistingContactWidget";
import FundChangeRow from "./FundChangeRow";
import type { MemberChangeView } from "./membersChangeRequestHelper";
import { groupFundChanges } from "./membersChangeRequestHelper";

interface Props {
  member: MemberChangeView;
  expanded: boolean;
  onToggleExpanded: () => void;
  contactHref?: string;
  isPending: boolean;
}

const getActionChipColor = (theme: Theme, action: MemberChangeAction): string => {
  switch (action) {
    case "UpdateExisting":
      return theme.palette.warning.main;
    case "Delete":
      return theme.palette.error.main;
    case "CreateNew":
      return theme.palette.info.main;
  }
};

const getActionChipLabel = (member: MemberChangeView): string => {
  switch (member.action) {
    case "CreateNew":
      return "Adding";
    case "Delete":
      return "Removing";
    default:
      return `${member.changeCount} ${pluralize("Change", member.changeCount)}`;
  }
};

interface NameDiffChipProps {
  label: string;
  variant: "old" | "new";
}

const NameDiffChip = ({ label, variant }: NameDiffChipProps) => (
  <Chip
    label={label}
    sx={(t) => {
      const color = variant === "old" ? t.palette.error.main : t.palette.success.main;
      return {
        bgcolor: makeTintedBackgroundFromColor(t, color),
        color,
        ...(variant === "old" && { textDecoration: "line-through" }),
      };
    }}
  />
);

const MemberChangeRow = ({ member, expanded, onToggleExpanded, contactHref, isPending }: Props) => {
  const displayName = member.name || member.email || "Existing contact";
  const isRemoval = member.action === "Delete";
  const fundGroups = groupFundChanges(member.fundChanges);
  const matchedContact = member.action === "CreateNew" ? member.existingContact : undefined;
  const nameChanged = !!matchedContact?.name && matchedContact.name !== member.name;
  // Contact-match hints describe what will happen on approval, so they only apply while the request is pending.
  const showMatchedContact = isPending && !!matchedContact;

  return (
    <>
      <TableRow sx={{ "& td": { borderBottom: 0 } }}>
        <TableCell sx={{ width: 48, px: 0.75 }}>
          <ExpandCollapseButton
            expanded={expanded}
            onToggle={onToggleExpanded}
            ExpandIcon={ExpandIcon}
            CollapseIcon={CollapseIcon}
            sx={{ my: -1 }}
            aria-label={expanded ? "Collapse contact details" : "Expand contact details"}
          />
        </TableCell>
        <TableCell>
          <Stack direction="row" spacing={1.5} sx={{ alignItems: "center" }}>
            <UserAvatar size="small" userName={displayName} />
            <Stack sx={{ flex: 1 }}>
              <Stack direction="row" spacing={1} sx={{ alignItems: "center" }}>
                {contactHref ? (
                  <Link
                    component={RouterLink}
                    to={contactHref}
                    target="_blank"
                    rel="noopener noreferrer"
                    underline="none"
                    variant="subtitle2"
                  >
                    {displayName}
                  </Link>
                ) : (
                  <Typography variant="subtitle2">{displayName}</Typography>
                )}
                <StatusChip label={getActionChipLabel(member)} color={(t) => getActionChipColor(t, member.action)} />
              </Stack>
              {member.email && (
                <Typography variant="caption" color="textSecondary">
                  {member.email}
                </Typography>
              )}
            </Stack>
            {showMatchedContact && (
              <Stack direction="row" spacing={1} sx={{ alignItems: "center", color: "warning.main" }}>
                <WarningAmberRoundedIcon />
                <Typography>Existing contact matched</Typography>
              </Stack>
            )}
          </Stack>
        </TableCell>
      </TableRow>

      <TableRow>
        <TableCell colSpan={2} sx={{ "&.MuiTableCell-root.MuiTableCell-body": { p: 0 } }}>
          <Collapse in={expanded} timeout="auto" unmountOnExit>
            <Stack spacing={2} sx={{ pl: 8, pr: 2, py: 2 }}>
              {showMatchedContact && (
                <Stack spacing={2}>
                  {nameChanged && (
                    <Stack spacing={1}>
                      <Typography color="textSecondary">
                        Name in the request differs from the record on file. Its current name will be kept:
                      </Typography>
                      <Stack direction="row" spacing={1} sx={{ alignItems: "center" }}>
                        <Typography variant="caption" color="textSecondary">
                          Full Name
                        </Typography>
                        <NameDiffChip label={member.name} variant="old" />
                        <NameDiffChip label={matchedContact.name} variant="new" />
                      </Stack>
                    </Stack>
                  )}
                  <Typography color="textSecondary">
                    Access in this request will be added to this existing contact — no new record will be created.
                  </Typography>
                  <Stack spacing={1}>
                    <Typography variant="subtitle2">Existing Contact</Typography>
                    <ExistingContactWidget contact={matchedContact} />
                  </Stack>
                </Stack>
              )}
              <Stack spacing={1}>
                {showMatchedContact && <Typography variant="subtitle2">New Access</Typography>}
                <RoundedTable withoutColumnBorders>
                  <TableHead>
                    <TableRow>
                      <TableCell sx={{ width: "40%" }}>Funds</TableCell>
                      <TableCell sx={(t) => ({ color: isRemoval ? t.palette.error.light : t.palette.success.light })}>
                        {isRemoval ? "Removed" : "Requested"}
                      </TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {fundGroups.map((group) => (
                      <FundChangeRow key={group.key} group={group} action={member.action} />
                    ))}
                  </TableBody>
                </RoundedTable>
              </Stack>
            </Stack>
          </Collapse>
        </TableCell>
      </TableRow>
    </>
  );
};

export default MemberChangeRow;
